import { Fragment, type ReactNode } from 'react';
import { CodeBlock, Heading, Link, Stack, Text } from '../ui';
import { parseMarkdownBlocks } from '../lib/markdownBlocks';

/**
 * A small, dependency-free Markdown renderer for research reports (§13.1, WP-R5).
 *
 * The report body is a single Markdown document produced by `renderReportMarkdown`
 * (shared) — a deterministic subset: `#`/`##`/`###` headings, paragraphs, numbered
 * and bulleted lists, inline `[text](url)` links, `**bold**`, and `` `code` ``. We
 * render exactly that subset to the `@/ui` design layer (no Tailwind/raw CSS), and
 * MUST stay self-contained: no external Markdown lib is pulled in (keeps the bundle
 * dependency-light and the PDF/print path CDN-free per the issue). The block-parse
 * stage is shared with the print/PDF emitter via `parseMarkdownBlocks` so the two
 * renders can never diverge.
 *
 * This is deliberately NOT a full CommonMark engine — it renders the exact shape the
 * shared renderer emits. Anything it does not recognise falls through as plain text,
 * so it degrades gracefully rather than throwing.
 */

/**
 * Split a bare URL match into the URL proper and any trailing characters that
 * belong to the surrounding prose rather than the link — sentence punctuation
 * (`. , ; : ! ?`), quotes, and *unbalanced* closing brackets (so a URL such as
 * `…/Foo_(bar)` keeps its `)`, but a URL wrapped in `(…)` does not swallow the
 * closer). Returns `[url, trailing]`.
 */
function splitTrailingPunctuation(raw: string): [string, string] {
  let end = raw.length;
  while (end > 0) {
    const ch = raw.charAt(end - 1);
    if ('.,;:!?\'"'.includes(ch)) {
      end -= 1;
      continue;
    }
    if (ch === ')' || ch === ']' || ch === '}') {
      const open = ch === ')' ? '(' : ch === ']' ? '[' : '{';
      const slice = raw.slice(0, end);
      const opens = slice.split(open).length - 1;
      const closes = slice.split(ch).length - 1;
      if (closes > opens) {
        end -= 1;
        continue;
      }
    }
    break;
  }
  return [raw.slice(0, end), raw.slice(end)];
}

/** Render inline spans: `[text](url)` links, bare `http(s)://` URLs, `**bold**`, and `` `code` ``. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  // One combined matcher walked left-to-right so spans compose in document order.
  // The `[text](url)` alternative is tried first, so a URL already inside a
  // Markdown link is consumed whole and never re-matched as a bare URL.
  const pattern = /\[([^\]]+)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|(https?:\/\/[^\s<]+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(<Fragment key={`${keyPrefix}-t${i}`}>{text.slice(lastIndex, match.index)}</Fragment>);
    }
    const [, linkText, linkUrl, bold, code, bareUrl] = match;
    if (linkUrl !== undefined && linkText !== undefined) {
      nodes.push(
        <Link key={`${keyPrefix}-l${i}`} href={linkUrl} external>
          {linkText}
        </Link>,
      );
    } else if (bareUrl !== undefined) {
      const [url, trailing] = splitTrailingPunctuation(bareUrl);
      nodes.push(
        <Link key={`${keyPrefix}-u${i}`} href={url} external>
          {url}
        </Link>,
      );
      if (trailing) {
        nodes.push(<Fragment key={`${keyPrefix}-ut${i}`}>{trailing}</Fragment>);
      }
    } else if (bold !== undefined) {
      nodes.push(
        <Text as="strong" key={`${keyPrefix}-b${i}`} weight="bold" style={{ display: 'inline' }}>
          {bold}
        </Text>,
      );
    } else if (code !== undefined) {
      nodes.push(
        <CodeBlock inline key={`${keyPrefix}-c${i}`} size="sm">
          {code}
        </CodeBlock>,
      );
    }
    lastIndex = pattern.lastIndex;
    i += 1;
  }
  if (lastIndex < text.length) {
    nodes.push(<Fragment key={`${keyPrefix}-tend`}>{text.slice(lastIndex)}</Fragment>);
  }
  return nodes;
}

/** Report headings stay modest (h4–h6) so they never compete with the page h1. */
const HEADING_STYLE = {
  1: { level: 4, size: 'lg' },
  2: { level: 5, size: 'md' },
  3: { level: 6, size: 'sm' },
} as const;

/** Render a research-report Markdown document to the `@/ui` type + layout primitives. */
export function Markdown({ text }: { text: string }) {
  const blocks = parseMarkdownBlocks(text);
  return (
    <Stack gap="3" data-testid="report-markdown">
      {blocks.map((block, i) => {
        if (block.type === 'heading') {
          const h = HEADING_STYLE[block.level];
          return (
            <Heading key={i} level={h.level} size={h.size}>
              {renderInline(block.text, `h${i}`)}
            </Heading>
          );
        }
        if (block.type === 'list') {
          return (
            <Stack
              key={i}
              as={block.ordered ? 'ol' : 'ul'}
              gap="1"
              style={{
                margin: 0,
                paddingInlineStart: '1.5rem',
                listStyleType: block.ordered ? 'decimal' : 'disc',
              }}
            >
              {block.items.map((item, j) => (
                <Text as="li" key={j} size="md" style={{ display: 'list-item' }}>
                  {renderInline(item, `li${i}-${j}`)}
                </Text>
              ))}
            </Stack>
          );
        }
        return (
          <Text as="p" key={i} size="md">
            {renderInline(block.text, `p${i}`)}
          </Text>
        );
      })}
    </Stack>
  );
}
